import { useState } from "react";
import { api } from "../api.js";
import { useApp } from "../app.jsx";
import { money, hours, fmtWhen, titleCase } from "../format.js";
import {
  Async, Avatar, Badge, Button, Card, Check, Empty, ErrorPanel, Field, Fields, Input, Modal, Ribbon,
  Select, Sep, Table, TopBar, Wrap, useAsync, useToast,
} from "../ui.jsx";

const ROLES = ["owner", "admin", "staff", "contractor", "viewer"];

/** Mirrors the permission map the API enforces — display only. */
const PERMS = [
  ["View dashboard & reports", true, true, false, false, true],
  ["Create & send invoices", true, true, false, false, false],
  ["Record payments", true, true, false, false, false],
  ["Log own time", true, true, true, true, false],
  ["See other people's time", true, true, false, false, true],
  ["See billing rates & revenue", true, true, false, false, true],
  ["Submit expenses", true, true, true, true, false],
  ["View & download everything (read-only)", true, true, false, false, true],
  ["Manage team & settings", true, false, false, false, false],
];

const roleTone = (role) =>
  role === "owner" ? "active" : role === "admin" ? "sent" : role === "viewer" ? "viewed" : "draft";

export default function Team() {
  const { me, settings, reloadApp } = useApp();
  const state = useAsync(() => api.get("team"), []);
  const [editing, setEditing] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [notice, setNotice] = useState(null);
  const sym = settings.currency_symbol;

  const refresh = () => {
    state.reload();
    reloadApp();
  };

  return (
    <>
      <TopBar title="Team">
        <Button kind="pri" onClick={() => setInviting(true)}>
          ＋ Invite teammate
        </Button>
      </TopBar>

      <Wrap>
        {notice && <Ribbon>{notice}</Ribbon>}

        <Async state={state}>
          {(people) => {
            const active = people.filter((u) => u.status === "active").length;
            const invited = people.filter((u) => u.status === "invited").length;

            return (
              <>
                <Card
                  bare
                  className={notice ? "mt" : ""}
                  title="People"
                  right={
                    <span className="sm muted">
                      {active} active
                      {invited ? ` · ${invited} pending invite${invited === 1 ? "" : "s"}` : ""}
                    </span>
                  }
                >
                  <Table
                    rows={people}
                    onRowClick={(u) => setEditing(u)}
                    empty={<Empty icon="◐">Nobody here yet</Empty>}
                    columns={[
                      {
                        key: "name",
                        label: "Name",
                        render: (u) => (
                          <div className="stack">
                            <Avatar name={u.name} />
                            <div>
                              <b>{u.name}</b>
                              {u.id === me.id && <span className="tiny muted"> (you)</span>}
                              <div className="tiny muted">{u.email}</div>
                            </div>
                          </div>
                        ),
                      },
                      {
                        key: "role",
                        label: "Role",
                        render: (u) => <Badge status={roleTone(u.role)} label={titleCase(u.role || "")} />,
                      },
                      {
                        key: "rate",
                        label: "Billing rate",
                        align: "right",
                        render: (u) =>
                          Number(u.billing_rate || 0) ? (
                            <span className="num">{money(u.billing_rate, sym)}/hr</span>
                          ) : (
                            <span className="muted">—</span>
                          ),
                      },
                      {
                        key: "month",
                        label: "Hours this month",
                        align: "right",
                        render: (u) =>
                          Number(u.minutes_this_month || 0) ? (
                            <span className="num">{hours(u.minutes_this_month)}</span>
                          ) : (
                            <span className="muted">—</span>
                          ),
                      },
                      {
                        key: "seen",
                        label: "Last active",
                        render: (u) => <span className="sm dim">{fmtWhen(u.last_seen_at) || "Never"}</span>,
                      },
                      { key: "status", label: "Status", render: (u) => <Badge status={u.status} /> },
                      { key: "go", label: "", align: "right", render: () => <span className="muted">›</span> },
                    ]}
                  />
                </Card>

                <Card className="mt" bare title="What each role can do">
                  <Table
                    rows={PERMS}
                    keyFn={(r) => r[0]}
                    columns={[
                      { key: "perm", label: "Permission", render: (r) => <span className="sm">{r[0]}</span> },
                      ...ROLES.map((role, i) => ({
                        key: role,
                        label: titleCase(role),
                        align: "right",
                        render: (r) =>
                          r[i + 1] ? (
                            <span style={{ color: "var(--ok)", fontWeight: 700 }}>✓</span>
                          ) : (
                            <span className="muted">—</span>
                          ),
                      })),
                    ]}
                  />
                </Card>
              </>
            );
          }}
        </Async>
      </Wrap>

      {editing && (
        <MemberModal
          user={editing}
          isMe={editing.id === me.id}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      )}

      {inviting && (
        <InviteModal
          onClose={() => setInviting(false)}
          onSaved={(note) => {
            setNotice(note);
            refresh();
          }}
        />
      )}
    </>
  );
}

/* -------------------------------------------------------------- edit member */

function MemberModal({ user, isMe, onClose, onSaved }) {
  const { settings } = useApp();
  const toast = useToast();
  const [form, setForm] = useState({
    name: user.name || "",
    role: user.role || "staff",
    billing_rate: user.billing_rate ?? "",
    billable_default: user.billable_default !== false,
    status: user.status || "active",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const pendingInvite = user.status === "invited" && !user.last_seen_at;

  async function run(fn, message) {
    setError(null);
    setBusy(true);
    try {
      const res = await fn();
      toast.push(res?.note || message);
      onSaved();
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  const save = () =>
    run(
      () => api.put(`team/${user.id}`, { ...form, billing_rate: Number(form.billing_rate) || 0 }),
      `${form.name || user.email} updated`
    );
  const resendInvite = () => run(() => api.post(`team/${user.id}/invite`, {}), "Invite email re-sent");
  const cancelInvite = () => run(() => api.del(`team/${user.id}`), "Invite cancelled");

  return (
    <Modal
      title={user.name}
      width={480}
      onClose={onClose}
      footer={
        <>
          {pendingInvite && (
            <Button kind="dgr" busy={busy} style={{ marginRight: "auto" }} onClick={cancelInvite}>
              Cancel invite
            </Button>
          )}
          {pendingInvite && (
            <Button busy={busy} onClick={resendInvite}>
              Resend invite
            </Button>
          )}
          <Button onClick={onClose}>Cancel</Button>
          <Button kind="pri" busy={busy} onClick={save}>
            Save changes
          </Button>
        </>
      }
    >
      <Fields>
        {error && <ErrorPanel error={error} />}
        <div className="row" style={{ gap: 12 }}>
          <Avatar name={user.name} size={44} />
          <div>
            <b>{user.name}</b>
            <div className="sm muted">{user.email}</div>
          </div>
        </div>
        {pendingInvite && (
          <Ribbon>
            They haven't accepted their sign-in invite yet. Resend it if the email got lost, or cancel to revoke
            the emailed link and remove this seat.
          </Ribbon>
        )}
        <Field label="Name">
          <Input value={form.name} onChange={(e) => set("name", e.target.value)} />
        </Field>
        <Fields cols={2}>
          <Field label="Role" hint={isMe ? "You cannot remove your own owner role." : undefined}>
            <Select value={form.role} onChange={(e) => set("role", e.target.value)}>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {titleCase(r)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Default billing rate">
            <Input
              className="i right num"
              value={form.billing_rate}
              onChange={(e) => set("billing_rate", e.target.value)}
            />
          </Field>
        </Fields>
        <Check
          label="Time logged by this person is billable by default"
          checked={form.billable_default}
          onChange={(v) => set("billable_default", v)}
        />
        <Field label="Status">
          <Select value={form.status} onChange={(e) => set("status", e.target.value)} disabled={isMe}>
            <option value="active">Active</option>
            <option value="invited">Invited</option>
            <option value="disabled">Disabled (no access)</option>
          </Select>
        </Field>
        <Sep />
        <div className="row">
          <span className="sm muted">Hours logged this month</span>
          <div className="grow" />
          <b className="num">{hours(user.minutes_this_month)}</b>
        </div>
        <div className="row">
          <span className="sm muted">Rate on file</span>
          <div className="grow" />
          <b className="num">{money(user.billing_rate, settings.currency_symbol)}/hr</b>
        </div>
      </Fields>
    </Modal>
  );
}

/* -------------------------------------------------------------- invite */

function InviteModal({ onClose, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState({ name: "", email: "", role: "staff", billing_rate: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  async function invite() {
    setError(null);
    setBusy(true);
    try {
      const res = await api.post("team", { ...form, billing_rate: Number(form.billing_rate) || 0 });
      const note = res.note || "Teammate added.";
      toast.push(note);
      onSaved(note);
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Invite a teammate"
      width={480}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button kind="pri" busy={busy} onClick={invite}>
            Add &amp; send invite
          </Button>
        </>
      }
    >
      <Fields>
        {error && <ErrorPanel error={error} />}
        <Fields cols={2}>
          <Field label="Name">
            <Input value={form.name} placeholder="Jordan Lee" onChange={(e) => set("name", e.target.value)} />
          </Field>
          <Field label="Email address">
            <Input
              type="email"
              value={form.email}
              placeholder="name@yourcompany.com"
              onChange={(e) => set("email", e.target.value)}
            />
          </Field>
        </Fields>
        <Fields cols={2}>
          <Field label="Role">
            <Select value={form.role} onChange={(e) => set("role", e.target.value)}>
              {ROLES.filter((r) => r !== "owner").map((r) => (
                <option key={r} value={r}>
                  {titleCase(r)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Billing rate">
            <Input
              className="i right num"
              value={form.billing_rate}
              placeholder="120"
              onChange={(e) => set("billing_rate", e.target.value)}
            />
          </Field>
        </Fields>
        <Ribbon>
          Staff can log their own time and submit expenses, but cannot see revenue, rates or other people's hours.
        </Ribbon>
        <Ribbon>
          Adding them emails the sign-in invite right away. You can resend or cancel it from their entry in the
          People list until they accept.
        </Ribbon>
      </Fields>
    </Modal>
  );
}
